<template>
  <div class="card card-body shadow-sm mt-0">
    <!-- Cabeçalho -->
    <div class="form-header d-flex justify-content-between align-items-center mb-4">
      <h4 class="form-title">
        {{ perfil.id ? 'Editar Perfil de Usuário' : 'Novo Perfil de Usuário' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm
      :disabled="!isFormValid || isSubmitting"
      :isSubmitting="isSubmitting"
      @submit="salvarPerfil"
      @cancel="voltar"
    >
      <!-- Dados do Perfil agora em form-section -->
      <div class="card card-body mb-4 w-100 form-section">
        <h6 class="form-section-title mb-3">Dados do Perfil</h6>
        <div class="row">
          <div class="col-md-4">
            <InputField
              v-model="perfil.nome"
              label="Nome do Perfil <span class='required'>*</span>"
              id="nome"
              placeholder="Digite o nome do perfil"
              :class="{ 'error-input': errors.nome }"
              required
            />
            <span v-if="errors.nome" class="error-message">{{ errors.nome }}</span>
          </div>
          <div class="col-md-5">
            <InputField
              v-model="perfil.descricao"
              label="Descrição <span class='required'>*</span>"
              id="descricao"
              placeholder="Digite a descrição"
              :class="{ 'error-input': errors.descricao }"
              required
            />
            <span v-if="errors.descricao" class="error-message">{{ errors.descricao }}</span>
          </div>
          <div class="col-md-2">
            <InputField
              v-model="perfil.peso"
              label="Peso <span class='required'>*</span>"
              id="peso"
              type="number"
              placeholder="Peso"
              :class="{ 'error-input': errors.peso }"
              required
            />
            <span v-if="errors.peso" class="error-message">{{ errors.peso }}</span>
          </div>
        </div>
      </div>

      <!-- Permissões por Grupo -->
      <div
        v-for="grupoPerm in permissoes"
        :key="grupoPerm.grupo"
        class="card card-body mb-4 w-100 form-section"
      >
        <h6 class="form-section-title mb-3">{{ grupoPerm.grupo }}</h6>

        <div v-if="grupoPerm.grupo === 'Gestão' && !granular" class="alert alert-info">
          Perfil legado: as seleções abaixo mostram os acessos atuais. Converter permite escolher cada item, inclusive desmarcar todos. A mudança só será persistida ao salvar.
          <button type="button" class="btn btn-outline-primary mt-2" @click="converterGestao">Converter para permissões individuais</button>
        </div>
        <!-- Permissões individuais -->
        <div class="grid-permissoes mb-3">
          <div
            v-for="categoria in grupoPerm.categorias"
            :key="categoria.nome"
            class="categoria-bloco"
          >
            <h6 class="mt-2 categoria-titulo">{{ categoria.nome }}</h6>
            <div
              class="form-check permissao-item"
              v-for="acao in categoria.acoes"
              :key="acao.value"
            >
              <input
                type="checkbox"
                class="form-check-input"
                :checked="permissionChecked(grupoPerm, acao.value)"
                :disabled="grupoPerm.grupo === 'Gestão' && !granular && acao.value !== 'menu_gestao'"
                @change="togglePermission(grupoPerm, categoria, acao.value, $event.target.checked)"
                :value="acao.value"
                :id="`permissao-${acao.value}`"
              />
              <label class="form-check-label" :for="`permissao-${acao.value}`">
                {{ acao.text }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  </div>
</template>

<script>
import { gestaoPermissions } from '@/config/gestaoPermissions'
import { MARKER, allowed, catalog, convert } from '@/utils/gestaoPolicy'
import { reactive, ref, computed, onMounted } from 'vue'
import InputField from '@/components/Layout/InputField.vue'
// import FormSection from '@/components/Layout/FormSection.vue'
import BaseForm from '@/components/Layout/BaseForm.vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/services/axios'

export default {
  components: { InputField, BaseForm },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const perfil = reactive({
      id: null,
      nome: '',
      descricao: '',
      peso: '',
      permissoes: [],
    })

    // Permissões agrupadas por domínio
    const permissoes = ref([
      {
        grupo: 'Administrador',
        categorias: [
          {
            nome: 'É usuário administrador?',
            acoes: [{ value: 'administrador', text: 'Sim' }],
          },
        ],
      },

      {
        grupo: 'Dashboard',
        categorias: [
          {
            nome: 'Cards',
            acoes: [
              { value: 'cards_aulas_avulsas', text: 'Aulas Avuslas' },
              { value: 'cards_aulas_reposicao', text: 'Aulas de Reposição' },
              { value: 'cards_aulas_experimentais', text: 'Aulas Experimentais' },
              { value: 'cards_faltas_avisadas', text: 'Faltas Avisadas' },
              { value: 'cards_plano_vencer', text: 'Planos a Vencer' },
              { value: 'cards_prospects', text: 'Prospects' },
              { value: 'cards_matriculas_ativas', text: 'Matrículas Ativas' },
              { value: 'cards_trocas_nivel_turma', text: 'Trocas de Nível' },
            ],
          },
          {
            nome: 'Gráficos',
            acoes: [{ value: 'graficos_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Agenda Diária de Turmas',
            acoes: [{ value: 'agendas_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Supervisões',
            acoes: [{ value: 'supervisoes_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Quedas não Comunicadas',
            acoes: [{ value: 'quedas_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Protocolos de Queda',
            acoes: [{ value: 'protocolos_queda_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Faltas não Comunicadas',
            acoes: [{ value: 'faltas_dashboard', text: 'Exibir' }],
          },
          {
            nome: 'Renovação Antecipada',
            acoes: [{ value: 'renovacao_antecipada_dashboard', text: 'Exibir' }],
          },

          {
            nome: 'Envio e-mail Troca de Nível / Certificado',
            acoes: [{ value: 'troca_nivel_envio_dashboard', text: 'Exibir' }],
          },

          {
            nome: 'Envio de Avaliações',
            acoes: [{ value: 'enviar_avaliacoes', text: 'Exibir' }],
          },

        ],
      },

      gestaoPermissions,

      {
        grupo: 'Cadastros',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_cadastro', text: 'Exibir' }],
          },

          {
            nome: 'Animais',
            acoes: [
              { value: 'listar_animais', text: 'Listar' },
              { value: 'adicionar_animais', text: 'Adicionar' },
              { value: 'alterar_animais', text: 'Alterar' },
              { value: 'excluir_animais', text: 'Excluir' },
              { value: 'detalhar_animais', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Brindes',
            acoes: [
              { value: 'listar_brindes', text: 'Listar' },
              { value: 'adicionar_brindes', text: 'Adicionar' },
              { value: 'alterar_brindes', text: 'Alterar' },
              { value: 'excluir_brindes', text: 'Excluir' },
              { value: 'detalhar_brindes', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Cargos de Funcionários',
            acoes: [
              { value: 'listar_cargos_funcionarios', text: 'Listar' },
              { value: 'adicionar_cargos_funcionarios', text: 'Adicionar' },
              { value: 'alterar_cargos_funcionarios', text: 'Alterar' },
              { value: 'excluir_cargos_funcionarios', text: 'Excluir' },
              { value: 'detalhar_cargos_funcionarios', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Categorias de Planos',
            acoes: [
              { value: 'listar_categorias_planos', text: 'Listar' },
              { value: 'adicionar_categorias_planos', text: 'Adicionar' },
              { value: 'alterar_categorias_planos', text: 'Alterar' },
              { value: 'excluir_categorias_planos', text: 'Excluir' },
              { value: 'detalhar_categorias_planos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Categorias de Produtos',
            acoes: [
              { value: 'listar_categorias_produtos', text: 'Listar' },
              { value: 'adicionar_categorias_produtos', text: 'Adicionar' },
              { value: 'alterar_categorias_produtos', text: 'Alterar' },
              { value: 'excluir_categorias_produtos', text: 'Excluir' },
              { value: 'detalhar_categorias_produtos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Convênios',
            acoes: [
              { value: 'listar_convenios', text: 'Listar' },
              { value: 'adicionar_convenios', text: 'Adicionar' },
              { value: 'alterar_convenios', text: 'Alterar' },
              { value: 'excluir_convenios', text: 'Excluir' },
              { value: 'detalhar_convenios', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Descontos',
            acoes: [
              { value: 'listar_descontos', text: 'Listar' },
              { value: 'adicionar_descontos', text: 'Adicionar' },
              { value: 'alterar_descontos', text: 'Alterar' },
              { value: 'excluir_descontos', text: 'Excluir' },
              { value: 'detalhar_descontos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Funcionários',
            acoes: [
              { value: 'listar_funcionarios', text: 'Listar' },
              { value: 'adicionar_funcionarios', text: 'Adicionar' },
              { value: 'alterar_funcionarios', text: 'Alterar' },
              { value: 'excluir_funcionarios', text: 'Excluir' },
              { value: 'detalhar_funcionarios', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Níveis de Turmas',
            acoes: [
              { value: 'listar_niveis_turmas', text: 'Listar' },
              { value: 'adicionar_niveis_turmas', text: 'Adicionar' },
              { value: 'alterar_niveis_turmas', text: 'Alterar' },
              { value: 'excluir_niveis_turmas', text: 'Excluir' },
              { value: 'detalhar_niveis_turmas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Período de Avaliações',
            acoes: [
              { value: 'listar_periodos_avaliacoes', text: 'Listar' },
              { value: 'adicionar_periodos_avaliacoes', text: 'Adicionar' },
              { value: 'alterar_periodos_avaliacoes', text: 'Alterar' },
              { value: 'excluir_periodos_avaliacoes', text: 'Excluir' },
              { value: 'detalhar_periodos_avaliacoes', text: 'Detalhar' },
              { value: 'aplicar_periodos_avaliacoes', text: 'Aplicar' },
            ],
          },

          {
            nome: 'Pessoas Físicas',
            acoes: [
              { value: 'listar_pessoas_fisicas', text: 'Listar' },
              { value: 'adicionar_pessoa_fisica', text: 'Adicionar' },
              { value: 'alterar_pessoa_fisica', text: 'Alterar' },
              { value: 'excluir_pessoa_fisica', text: 'Excluir' },
              { value: 'detalhar_pessoa_fisica', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Pessoas Jurídicas',
            acoes: [
              { value: 'listar_pessoas_juridicas', text: 'Listar' },
              { value: 'adicionar_pessoa_juridica', text: 'Adicionar' },
              { value: 'alterar_pessoa_juridica', text: 'Alterar' },
              { value: 'excluir_pessoa_juridica', text: 'Excluir' },
              { value: 'detalhar_pessoa_juridica', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Planos de Matrículas',
            acoes: [
              { value: 'listar_planos_matriculas', text: 'Listar' },
              { value: 'adicionar_planos_matriculas', text: 'Adicionar' },
              { value: 'alterar_planos_matriculas', text: 'Alterar' },
              { value: 'excluir_planos_matriculas', text: 'Excluir' },
              { value: 'detalhar_planos_matriculas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Produtos e Estoque',
            acoes: [
              { value: 'listar_produtos', text: 'Listar' },
              { value: 'adicionar_produtos', text: 'Adicionar' },
              { value: 'alterar_produtos', text: 'Alterar' },
              { value: 'excluir_produtos', text: 'Excluir' },
              { value: 'detalhar_produtos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Tipos de Planos',
            acoes: [
              { value: 'listar_tipos_planos', text: 'Listar' },
              { value: 'adicionar_tipos_planos', text: 'Adicionar' },
              { value: 'alterar_tipos_planos', text: 'Alterar' },
              { value: 'excluir_tipos_planos', text: 'Excluir' },
              { value: 'detalhar_tipos_planos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Turmas',
            acoes: [
              { value: 'listar_turmas', text: 'Listar' },
              { value: 'adicionar_turmas', text: 'Adicionar' },
              { value: 'alterar_turmas', text: 'Alterar' },
              { value: 'excluir_turmas', text: 'Excluir' },
              { value: 'detalhar_turmas', text: 'Detalhar' },
            ],
          },

        ],
      },
      {
        grupo: 'Operacional',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_operacional', text: 'Exibir' }],
          },
          {
            nome: 'Aniversariantes',
            acoes: [{ value: 'listar_aniversariantes', text: 'Listar' }],
          },
          {
            nome: 'Aulas Avulsas',
            acoes: [
              { value: 'listar_aulas_avulsas', text: 'Listar' },
              { value: 'adicionar_aulas_avulsas', text: 'Adicionar' },
              { value: 'alterar_aulas_avulsas', text: 'Alterar' },
              { value: 'cancelar_aulas_avulsas', text: 'Cancelar' },
              { value: 'detalhar_aulas_avulsas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Aulas de Reposição',
            acoes: [
              { value: 'listar_aulas_reposicao', text: 'Listar' },
              { value: 'adicionar_aulas_reposicao', text: 'Adicionar' },
              { value: 'alterar_aulas_reposicao', text: 'Alterar' },
              { value: 'excluir_aulas_reposicao', text: 'Excluir' },
              { value: 'detalhar_aulas_reposicao', text: 'Detalhar' },
              { value: 'reagendar_aulas_reposicao', text: 'Reagendar' },
            ],
          },
          {
            nome: 'Avaliações de Desempenho',
            acoes: [
              { value: 'listar_avaliacoes_desempenho', text: 'Listar' },
              { value: 'alterar_avaliacoes_desempenho', text: 'Alterar' },
              { value: 'detalhar_avaliacoes_desempenho', text: 'Detalhar' },
            ],
          },

          {
            nome: 'Responsáveis Ativos',
            acoes: [
              { value: 'listar_emarketing_respativos', text: 'Listar' },
            ],
          },

          {
            nome: 'Responsáveis Inativos',
            acoes: [
              { value: 'listar_emarketing_respinativos', text: 'Listar' },
            ],
          },

          {
            nome: 'Matrículas',
            acoes: [
              { value: 'listar_matriculas', text: 'Listar' },
              { value: 'adicionar_matriculas', text: 'Adicionar' },
              { value: 'operar_matriculas', text: 'Criar, alterar e renovar com recebimentos (somente matrículas)' },
              { value: 'alterar_matriculas', text: 'Alterar' },
              { value: 'excluir_matriculas', text: 'Excluir' },
              { value: 'detalhar_matriculas', text: 'Detalhar' },
              { value: 'trancar_matriculas', text: 'Trancar' },
              { value: 'destrancar_matriculas', text: 'Destrancar' },
              { value: 'cancelar_matriculas', text: 'Cancelar' },
              { value: 'reverter_cancelamento_matriculas', text: 'Reverter Cancelamento' },
              { value: 'alterar_direito_imagem_matriculas', text: 'Alterar imagem' },
            ],
          },
          {
            nome: 'Pós-vendas',
            acoes: [
              { value: 'listar_posvendas', text: 'Listar' },
              { value: 'adicionar_posvendas', text: 'Adicionar' },
              { value: 'realizar_posvendas', text: 'Realizar' },
              { value: 'alterar_posvendas', text: 'Alterar' },
              { value: 'excluir_posvendas', text: 'Excluir' },
              { value: 'detalhar_posvendas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Prospecções',
            acoes: [
              { value: 'listar_prospeccao', text: 'Listar' },
              { value: 'adicionar_prospeccao', text: 'Adicionar' },
              { value: 'alterar_prospeccao', text: 'Alterar' },
              { value: 'excluir_prospeccao', text: 'Excluir' },
              { value: 'detalhar_prospeccao', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Protocolos de Queda',
            acoes: [
              { value: 'listar_protocolos_queda', text: 'Listar' },
            ],
          },
          // {
          //   nome: 'Saídas de Animais',
          //   acoes: [
          //     { value: 'listar_saidas_animais', text: 'Listar' },
          //     { value: 'adicionar_saidas_animais', text: 'Adicionar' },
          //     { value: 'alterar_saidas_animais', text: 'Alterar' },
          //     { value: 'excluir_saidas_animais', text: 'Excluir' },
          //     { value: 'detalhar_saidas_animais', text: 'Detalhar' },
          //   ],
          // },
          {
            nome: 'Renovações - Cancelamento',
            acoes: [
              { value: 'cancelar_renovacao', text: 'Cancelar' },
            ],
          },

          {
            nome: 'Supervisões',
            acoes: [
              { value: 'listar_supervisoes', text: 'Listar' },
              { value: 'alterar_supervisoes', text: 'Alterar' },
              { value: 'detalhar_supervisoes', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Turmas do Instrutor',
            acoes: [
              { value: 'listar_turmas_instrutor', text: 'Listar' },
              { value: 'registrar_chamada', text: 'Registrar Chamada' },
              { value: 'imprimir_chamada', text: 'Imprimir Chamada' },
              { value: 'detalhar_turmas_instrutor', text: 'Detalhar' },
            ],
          },
        ],
      },
      {
        grupo: 'Loja',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_loja', text: 'Exibir' }],
          },
          {
            nome: 'Entrada de Produtos',
            acoes: [
              { value: 'listar_entradas', text: 'Listar' },
              { value: 'adicionar_entradas', text: 'Adicionar' },
              { value: 'alterar_entradas', text: 'Alterar' },
              { value: 'excluir_entradas', text: 'Excluir' },
              { value: 'detalhar_entradas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Vendas de Produtos',
            acoes: [
              { value: 'listar_vendas', text: 'Listar' },
              { value: 'adicionar_vendas', text: 'Adicionar' },
              { value: 'alterar_vendas', text: 'Alterar' },
              { value: 'excluir_vendas', text: 'Excluir' },
              { value: 'detalhar_vendas', text: 'Detalhar' },
              { value: 'emitir_recibo_vendas', text: 'Emitir Recibo' },
            ],
          },
        ],
      },
      {
        grupo: 'Financeiro',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_financeiro', text: 'Exibir' }],
          },
          {
            nome: 'Cobranças e Boletos',
            acoes: [
              {
                value: 'financeiro_cobrancas_visualizar',
                text: 'Visualizar',
              },
              {
                value: 'financeiro_cobrancas_operar',
                text: 'Operar',
              },
              {
                value: 'financeiro_cobrancas_configurar',
                text: 'Configurar',
              },
            ],
          },
          {
            nome: 'Contas Financeiras',
            acoes: [
              { value: 'listar_contas_financeiras', text: 'Listar' },
              { value: 'adicionar_contas_financeiras', text: 'Adicionar' },
              { value: 'alterar_contas_financeiras', text: 'Alterar' },
              { value: 'excluir_contas_financeiras', text: 'Excluir' },
              { value: 'detalhar_contas_financeiras', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Conciliação Bancária',
            acoes: [
              { value: 'listar_conciliacao_bancaria', text: 'Listar' },
              { value: 'importar_conciliacao_bancaria', text: 'Importar arquivos (conciliação automática exige Executar)' },
              { value: 'executar_conciliacao_bancaria', text: 'Executar conciliações' },
              { value: 'desfazer_conciliacao_bancaria', text: 'Desfazer vínculos' },
              { value: 'ajustar_conciliacao_bancaria', text: 'Ajustes contábeis (exige também a permissão financeira da ação)' },
              {
                value: 'gerenciar_identidades_conciliacao_bancaria',
                text: 'Gerenciar identidades'
              },
            ],
          },
          {
            nome: 'Despesas',
            acoes: [
              { value: 'listar_despesas', text: 'Listar' },
              { value: 'adicionar_despesas', text: 'Adicionar' },
              { value: 'alterar_despesas', text: 'Alterar' },
              { value: 'excluir_despesas', text: 'Excluir' },
              { value: 'detalhar_despesas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Planos de Contas',
            acoes: [
              { value: 'listar_planos_contas', text: 'Listar' },
              { value: 'adicionar_planos_contas', text: 'Adicionar' },
              { value: 'alterar_planos_contas', text: 'Alterar' },
              { value: 'excluir_planos_contas', text: 'Excluir' },
              { value: 'detalhar_planos_contas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Receitas Gerais',
            acoes: [
              { value: 'listar_receitas_gerais', text: 'Listar' },
              { value: 'adicionar_receitas_gerais', text: 'Adicionar' },
              { value: 'alterar_receitas_gerais', text: 'Alterar' },
              { value: 'excluir_receitas_gerais', text: 'Excluir' },
              { value: 'detalhar_receitas_gerais', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Receitas Planos',
            acoes: [
              { value: 'listar_receitas_planos', text: 'Listar' },
              { value: 'adicionar_receitas_planos', text: 'Adicionar' },
              { value: 'alterar_receitas_planos', text: 'Alterar' },
              { value: 'excluir_receitas_planos', text: 'Excluir' },
              { value: 'detalhar_receitas_planos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Registros Financeiros',
            acoes: [
              { value: 'listar_registros_financeiros', text: 'Listar' },
              { value: 'adicionar_registros_financeiros', text: 'Adicionar' },
              { value: 'alterar_registros_financeiros', text: 'Alterar' },
              { value: 'excluir_registros_financeiros', text: 'Excluir' },
              { value: 'detalhar_registros_financeiros', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Subcontas',
            acoes: [
              { value: 'listar_subcontas', text: 'Listar' },
              { value: 'adicionar_subcontas', text: 'Adicionar' },
              { value: 'alterar_subcontas', text: 'Alterar' },
              { value: 'excluir_subcontas', text: 'Excluir' },
              { value: 'detalhar_subcontas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Transferências entre Contas',
            acoes: [
              { value: 'listar_transferencias_contas', text: 'Listar' },
              { value: 'adicionar_transferencias_contas', text: 'Adicionar' },
              { value: 'alterar_transferencias_contas', text: 'Alterar' },
              { value: 'excluir_transferencias_contas', text: 'Excluir' },
              { value: 'detalhar_transferencias_contas', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Valores',
            acoes: [
              { value: 'listar_valores', text: 'Listar' },
              { value: 'adicionar_valores', text: 'Adicionar' },
              { value: 'alterar_valores', text: 'Alterar' },
              { value: 'excluir_valores', text: 'Excluir' },
              { value: 'detalhar_valores', text: 'Detalhar' },
            ],
          },
          // {
          //   nome: 'Operadores de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_operadores', text: 'Listar' },
          //     { value: 'adicionar_parametros_operadores', text: 'Adicionar' },
          //     { value: 'alterar_parametros_operadores', text: 'Alterar' },
          //     { value: 'excluir_parametros_operadores', text: 'Excluir' },
          //     { value: 'detalhar_parametros_operadores', text: 'Detalhar' },
          //   ],
          // },
          // {
          //   nome: 'Meios de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_meios', text: 'Listar' },
          //     { value: 'adicionar_parametros_meios', text: 'Adicionar' },
          //     { value: 'alterar_parametros_meios', text: 'Alterar' },
          //     { value: 'excluir_parametros_meios', text: 'Excluir' },
          //     { value: 'detalhar_parametros_meios', text: 'Detalhar' },
          //   ],
          // },
          // {
          //   nome: 'Formas de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_formas', text: 'Listar' },
          //     { value: 'adicionar_parametros_formas', text: 'Adicionar' },
          //     { value: 'alterar_parametros_formas', text: 'Alterar' },
          //     { value: 'excluir_parametros_formas', text: 'Excluir' },
          //     { value: 'detalhar_parametros_formas', text: 'Detalhar' },
          //   ],
          // },
          // {
          //   nome: 'Bandeiras de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_bandeiras', text: 'Listar' },
          //     { value: 'adicionar_parametros_bandeiras', text: 'Adicionar' },
          //     { value: 'alterar_parametros_bandeiras', text: 'Alterar' },
          //     { value: 'excluir_parametros_bandeiras', text: 'Excluir' },
          //     { value: 'detalhar_parametros_bandeiras', text: 'Detalhar' },
          //   ],
          // },
          // {
          //   nome: 'Tipos de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_tipos', text: 'Listar' },
          //     { value: 'adicionar_parametros_tipos', text: 'Adicionar' },
          //     { value: 'alterar_parametros_tipos', text: 'Alterar' },
          //     { value: 'excluir_parametros_tipos', text: 'Excluir' },
          //     { value: 'detalhar_parametros_tipos', text: 'Detalhar' },
          //   ],
          // },
          // {
          //   nome: 'Taxas de Pagamentos',
          //   acoes: [
          //     { value: 'listar_parametros_taxas', text: 'Listar' },
          //     { value: 'adicionar_parametros_taxas', text: 'Adicionar' },
          //     { value: 'alterar_parametros_taxas', text: 'Alterar' },
          //     { value: 'excluir_parametros_taxas', text: 'Excluir' },
          //     { value: 'detalhar_parametros_taxas', text: 'Detalhar' },
          //   ],
          // },
        ],
      },
      {
        grupo: 'Configurações',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_configuracoes', text: 'Exibir' }],
          },
          {
            nome: 'Documentos',
            acoes: [
              { value: 'listar_documentos', text: 'Listar' },
              { value: 'adicionar_documentos', text: 'Adicionar' },
              { value: 'alterar_documentos', text: 'Alterar' },
              { value: 'excluir_documentos', text: 'Excluir' },
              { value: 'detalhar_documentos', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Empresa',
            acoes: [{ value: 'alterar_empresa', text: 'Alterar' }],
          },
          {
            nome: 'Formulários Avaliações',
            acoes: [
              { value: 'listar_formularios_avaliacoes', text: 'Listar' },
              { value: 'adicionar_formularios_avaliacoes', text: 'Adicionar' },
              { value: 'alterar_formularios_avaliacoes', text: 'Alterar' },
              { value: 'excluir_formularios_avaliacoes', text: 'Excluir' },
              { value: 'detalhar_formularios_avaliacoes', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Parâmetros Categorias',
            acoes: [
              { value: 'listar_paramentros_categorias', text: 'Listar' },
              { value: 'adicionar_paramentros_categorias', text: 'Adicionar' },
              { value: 'alterar_paramentros_categorias', text: 'Alterar' },
              { value: 'excluir_paramentros_categorias', text: 'Excluir' },
              { value: 'detalhar_paramentros_categorias', text: 'Detalhar' },
            ],
          },

          {
            nome: 'Parâmetros Blocos',
            acoes: [
              { value: 'listar_paramentros_blocos', text: 'Listar' },
              { value: 'adicionar_paramentros_blocos', text: 'Adicionar' },
              { value: 'alterar_paramentros_blocos', text: 'Alterar' },
              { value: 'excluir_paramentros_blocos', text: 'Excluir' },
              { value: 'detalhar_paramentros_blocos', text: 'Detalhar' },
            ],
          },

          {
            nome: 'Parâmetros Questões',
            acoes: [
              { value: 'listar_paramentros_questoes', text: 'Listar' },
              { value: 'adicionar_paramentros_questoes', text: 'Adicionar' },
              { value: 'alterar_paramentros_questoes', text: 'Alterar' },
              { value: 'excluir_paramentros_questoes', text: 'Excluir' },
              { value: 'detalhar_paramentros_questoes', text: 'Detalhar' },
            ],
          },

          // {
          //   nome: 'Parâmetros Opções',
          //   acoes: [
          //     { value: 'listar_paramentros_opcoes', text: 'Listar' },
          //     { value: 'adicionar_paramentros_opcoes', text: 'Adicionar' },
          //     { value: 'alterar_paramentros_opcoes', text: 'Alterar' },
          //     { value: 'excluir_paramentros_opcoes', text: 'Excluir' },
          //     { value: 'detalhar_paramentros_opcoes', text: 'Detalhar' },
          //   ],
          // },
          {
            nome: 'Perfis de Usuários',
            acoes: [
              { value: 'listar_perfis_usuarios', text: 'Listar' },
              { value: 'adicionar_perfis_usuarios', text: 'Adicionar' },
              { value: 'alterar_perfis_usuarios', text: 'Alterar' },
              { value: 'excluir_perfis_usuarios', text: 'Excluir' },
              { value: 'detalhar_perfis_usuarios', text: 'Detalhar' },
            ],
          },
          {
            nome: 'Usuários',
            acoes: [
              { value: 'listar_usuarios', text: 'Listar' },
              { value: 'adicionar_usuarios', text: 'Adicionar' },
              { value: 'alterar_usuarios', text: 'Alterar' },
              { value: 'excluir_usuarios', text: 'Excluir' },
              { value: 'detalhar_usuarios', text: 'Detalhar' },
            ],
          },

          {
            nome: "Recessos",
            acoes: [
              { value: "listar_recessos", text: "Listar" },
              { value: "adicionar_recessos", text: "Adicionar" },
              { value: "alterar_recessos", text: "Alterar" },
              { value: "excluir_recessos", text: "Excluir" },
              { value: "detalhar_recessos", text: "Detalhar" },
              { value: "aplicar_recessos", text: "Aplicar" },
              { value: "cancelar_recessos", text: "Cancelar" },
            ],
          },






        ],
      },

      {
        grupo: 'Ajuda',
        categorias: [
          {
            nome: 'Menu Lateral',
            acoes: [{ value: 'menu_ajuda', text: 'Exibir' }],
          },

          {
            nome: 'Material',
            acoes: [
              { value: 'listar_material', text: 'Listar' },
              { value: 'adicionar_material', text: 'Adicionar' },
              { value: 'alterar_material', text: 'Alterar' },
              { value: 'excluir_material', text: 'Excluir' },
              { value: 'detalhar_material', text: 'Detalhar' },
            ],
          },

          {
            nome: 'Videoaulas [Manuais]',
            acoes: [
              { value: 'listar_videoaulas', text: 'Listar' },
              { value: 'adicionar_videoaulas', text: 'Adicionar' },
              { value: 'alterar_videoaulas', text: 'Alterar' },
              { value: 'excluir_videoaulas', text: 'Excluir' },
              { value: 'detalhar_videoaulas', text: 'Detalhar' },
            ],
          },

          // {
          //   nome: 'Quiz',
          //   acoes: [
          //     { value: 'exibir_quiz', text: 'Exibir' },
          //   ],
          // },

          // {
          //   nome: 'Montador de pista',
          //   acoes: [
          //     { value: 'exibir_montador_pista', text: 'Exibir' },
          //   ],
          // },

        ],
      },

    ])

    const errors = reactive({})
    const formTouched = ref(false)

    const isFormValid = computed(() => {
      return (
        perfil.nome &&
        perfil.descricao &&
        perfil.peso &&
        !Object.values(errors).some((e) => e)
      )
    })

    const carregarPerfil = async () => {
      const perfilId = route.params.id
      if (perfilId === 'novo' || !perfilId) {
        perfil.id = null
        perfil.nome = ''
        perfil.descricao = ''
        perfil.peso = ''
        perfil.permissoes = [MARKER]
        return
      }
      try {
        const { data } = await axios.get(
          `/configuracoes/perfis-de-usuarios/${perfilId}`
        )
        Object.assign(perfil, data)
        perfil.permissoes = data.permissions || []
      } catch (err) {
        console.error('Erro ao carregar o perfil:', err)
        alert('Erro ao carregar os dados do perfil.')
        router.push('/configuracoes/perfis-de-usuarios')
      }
    }

    const isSubmitting = ref(false)

    const salvarPerfil = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      const payload = {
        nome: perfil.nome,
        descricao: perfil.descricao,
        peso: perfil.peso,
        permissoes: perfil.permissoes,
      }
      try {
        const url = perfil.id
          ? `/configuracoes/perfis-de-usuarios/${perfil.id}`
          : `/configuracoes/perfis-de-usuarios`
        const method = perfil.id ? 'put' : 'post'
        await axios[method](url, payload)
        router.push('/configuracoes/perfis-de-usuarios')
      } catch (err) {
        console.error('Erro ao salvar perfil:', err)
        alert('Erro ao salvar perfil.')
      } finally {
        isSubmitting.value = false
      }
    }

    const voltar = () => {
      router.push('/configuracoes/perfis-de-usuarios')
    }

    onMounted(carregarPerfil)

    const granular = computed(() => perfil.permissoes.includes(MARKER))
    const converterGestao = () => { perfil.permissoes = convert(perfil.permissoes) }
    const permissionChecked = (group, permission) => {
      if (group.grupo === 'Gestão' && !granular.value && permission !== 'menu_gestao') {
        return allowed(perfil.permissoes, catalog.find(item => item.permission === permission))
      }
      return perfil.permissoes.includes(permission)
    }
    const togglePermission = (group, category, permission, checked) => {
      const selected = new Set(perfil.permissoes)
      if (checked) selected.add(permission)
      else selected.delete(permission)
      // Only the category the user edited may imply its existing Listar action.
      if (checked && group.grupo !== 'Gestão') {
        const listar = category.acoes.find(action => action.text === 'Listar')
        if (listar) selected.add(listar.value)
        const menu = group.categorias.find(cat => cat.nome === 'Menu Lateral')
        if (menu) selected.add(menu.acoes[0].value)
      }
      perfil.permissoes = [...selected]
    }


    return {
      perfil,
      permissoes,
      errors,
      isFormValid,
      formTouched,
      isSubmitting,
      salvarPerfil, granular, converterGestao, permissionChecked, togglePermission,
      voltar,
    }
  },
}
</script>

<style scoped>
.form-section-title {
  font-size: 0.9rem;    /* ajuste pra que fique do tamanho que você preferir */
  font-weight: 600;     /* opcional, deixa um pouco mais destacado */
  margin-bottom: 1rem;  /* opcional, aumenta o espaçamento abaixo */
}

.categoria-titulo {
  font-size: 0.85rem;
  font-weight: 600;
}
.permissao-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  margin-bottom: 2px;
  line-height: 1.4;
}
.permissao-item .form-check-input {
  transform: none;
  -webkit-transform: none;
}
.permissao-item .form-check-label {
  font-size: 0.85rem;
}
/* grid sempre com 5 colunas iguais */
.grid-permissoes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1rem;
  width: 100%;
}
/* ajuste do bloco de categoria */
.categoria-bloco {
  /* opcional: limite de largura interna */
  /* max-width: 200px; */
}

@media (max-width: 768px) {
  .col-md-2 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
@media (max-width: 576px) {
  .col-md-2 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>

<style scoped>
.permissao-item { display: flex; gap: 0.5rem; align-items: flex-start; margin-bottom: 0.5rem; }
.permissao-item .form-check-label { line-height: 1.4; white-space: normal; }
.permissao-item .form-check-input { flex-shrink: 0; }
</style>
